// The allocation rule of a batch: which designs a batch takes when the counts of
// its phase are auto-allocated (Bulk → Auto-allocate by prefix or type). A batch
// takes designs by **name prefix** or by **type**, never both
// (docs/timber-panel-tipos-proposal.md §5.10). Stored on the batch as
// `meta.match_by` / `meta.prefixes` / `meta.tipos`, and on each batch of a
// phase template (`company.meta.phase_templates[].batches[]`) with the same
// keys. A batch stored before types existed has no `match_by` and reads as
// `prefix`. Pure, so it can be unit tested.
import { DESIGN_TIPOS, GENERIC_TIPO } from "../components/fields/tipos.js"

export const MATCH_BY = ["prefix", "tipo"]

// The types a batch can take: every type a design can have, Generic first,
// then the assembly types and the module type.
export const BATCH_TIPOS = [GENERIC_TIPO, ...DESIGN_TIPOS.assembly.filter((t) => t !== GENERIC_TIPO),
    ...DESIGN_TIPOS.module.filter((t) => t !== GENERIC_TIPO)]

const clean_list = (list) =>
    [...new Set((Array.isArray(list) ? list : []).map((v) => String(v ?? "").trim()).filter(Boolean))]

/** `{match_by, prefixes, tipos}` of a batch's meta or a template batch. */
export function batchRule(source) {
    const match_by = MATCH_BY.includes(source?.match_by) ? source.match_by : "prefix"
    return {
        match_by,
        prefixes: clean_list(source?.prefixes),
        tipos: clean_list(source?.tipos).filter((t) => BATCH_TIPOS.includes(t)),
    }
}

/** What is stored: the mode and its own list, the other list emptied, so a
 * batch never carries a rule that is not used. */
export function batchRuleMeta(source) {
    const rule = batchRule(source)
    return {
        match_by: rule.match_by,
        prefixes: rule.match_by === "prefix" ? rule.prefixes : [],
        tipos: rule.match_by === "tipo" ? rule.tipos : [],
    }
}

/** Whether the batch takes any design: a prefix or a type in its mode. */
export function hasBatchRule(source) {
    const rule = batchRule(source)
    return rule.match_by === "tipo" ? rule.tipos.length > 0 : rule.prefixes.length > 0
}

/**
 * The batch a design goes to, or null. `batches` are the batches of one phase,
 * each with its `meta`. A batch matching a **name prefix** wins, the longest
 * prefix first (as before types existed); then the first batch, by priority,
 * whose **types** include the design's type. `design` is `{name, tipo}`.
 */
export function matchBatch(design, batches) {
    const name = String(design?.name || "")
    let best = null
    let best_len = -1
    for (const b of batches || []) {
        const rule = batchRule(b?.meta)
        if (rule.match_by !== "prefix") continue
        for (const prefix of rule.prefixes) {
            if (name.startsWith(prefix) && prefix.length > best_len) {
                best = b
                best_len = prefix.length
            }
        }
    }
    if (best) return best
    const tipo = design?.tipo || GENERIC_TIPO
    const by_priority = [...(batches || [])].sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0))
    return by_priority.find((b) => {
        const rule = batchRule(b?.meta)
        return rule.match_by === "tipo" && rule.tipos.includes(tipo)
    }) || null
}

/** The rule as a short text, `t` translating the type labels: the prefixes,
 * or the types; "" when the batch has none. */
export function batchRuleText(source, t) {
    const rule = batchRule(source)
    if (rule.match_by === "tipo") return rule.tipos.map((v) => t(`tipo_${v}`)).join(", ")
    return rule.prefixes.join(", ")
}
